import Container = Phaser.GameObjects.Container;
import Sprite = Phaser.GameObjects.Sprite;
import Point = Phaser.Geom.Point;
import { PathView } from '../components/pathView';
import { Game } from '../engine/game';
import { Scene } from 'phaser';
import { Coin } from '../engine/coin';
import { VineView } from '../components/vineView';
import { MonkeyView } from '../components/monkeyView';
import { CoinView } from '../components/coinView';
import { GameResult, Operation } from '../engine/types';
import { findShortestPath, generateRandomTarget } from '../pathFinder';
import { GroundView } from '../components/groundView';

export class GameScreen extends Container {
    private graphics: Phaser.GameObjects.Graphics;
    private coinsContainer: Phaser.GameObjects.Container;
    private simulate: Game;
    private container: Phaser.GameObjects.Container;
    private pathView: PathView;
    private vines: VineView[] = [];
    private slideContainer: Container;
    private monkey: MonkeyView;
    private mainCoins: CoinView[] = [];
    private playButton: Sprite;
    private ground: GroundView;
    private isWinning = false;

    private coinsSprites: CoinView[];
    private operations: Operation[] | null;
    private isFirstPlay: boolean;

    constructor(scene: Scene) {
        super(scene);
        this.pathView = new PathView(this.scene);
        this.add(this.pathView);
        this.initialize();
        this.scene.events.on('update', this.handleUpdate, this);
    }

    private handleUpdate(time, delta) {
        if (!this.simulate.isPlaying) return;
        if (this.coinsContainer && this.simulate.coinsContainer) {
            this.coinsContainer.x = this.simulate.coinsContainer.x;
            this.slideContainer.x = this.simulate.coinsContainer.x;
        }
        if (this.monkey) {
            this.monkey.y = this.simulate.monkey.position.y;
            this.monkey.x = 400;
            this.addVerticalityToCamera();
        }
        let coin: CoinView;
        if (this.coinsSprites) {
            for (let i = this.coinsSprites.length - 1; i >= 0; i--) {
                coin = this.coinsSprites[i];
                const { tx } = coin.getWorldTransformMatrix();
                if (tx < -100) {
                    coin?.destroy();
                    this.coinsSprites.splice(i, 1);
                }
            }
        }
    }

    private addVerticalityToCamera() {
        const { y } = this.simulate.monkey.position;
        const offset = 255 - y / 3;
        this.pathView.y = offset;
        this.container.y = offset;
        this.monkey.y += offset;
        this.monkey.y += 60;
    }

    private initialize(): void {
        this.isFirstPlay = true;
        this.container = this.scene.add.container();
        this.container.x = 400;
        this.add(this.container);
        this.slideContainer = this.scene.add.container();
        this.container.add(this.slideContainer);
        this.simulate = new Game({ x: 0, y: 350 }, 1920, 1080, 100);
        this.simulate.eventEmitter.on('hit', (coin, index) => {
            this.mainCoins[index].playHit();
            this.mainCoins.splice(index, 1);
            if (coin.operation.function === 'divide') {
                this.monkey.reactToBite();
            }
        });
        this.simulate.eventEmitter.on('swing', (curveIndex: number) => {
            this.vines[curveIndex].playAnimation();
            this.vines.splice(curveIndex, 1);
            this.monkey.playSwing();
        });
        this.graphics = this.scene.add.graphics();
        this.container.add(this.graphics);
        this.simulate.eventEmitter.on('lastSwing', (lastY, maxY, percent) => {
            if (this.isWinning) {
                this.scene.time.delayedCall(1654, () => {
                    this.monkey.playLanding();
                });
            } else {
                this.scene.time.delayedCall(1, () => {
                    this.monkey.playDeath();
                });
            }
        });

        this.simulate.eventEmitter.on('complete', () => {
            this.clearOld();
            this.scene.time.delayedCall(1844, () => {
                this.playButton.setVisible(true);
            });
        });
        this.initPlayButton();
    }

    private clearOld() {
        this.pathView.stop();

        this.coinsSprites.forEach((e) => e.destroy());
        this.coinsSprites = [];
        this.mainCoins = [];
        this.operations = [];
    }

    private startNew() {
        this.simulate.start(this.operations as Operation[], this.scene, this.container);
        this.initCoins(this.simulate.coins, this.simulate.mainCoins);
        this.pathView.start();
        this.initVines(this.simulate.vinePositions as any);
        this.initMonkey();
        this.initGround();
    }

    private initCoins(dummyCoins: Coin[], mainCoins: Coin[]): void {
        this.coinsContainer = this.scene.add.container();
        this.coinsContainer.x = 0;
        this.container.add(this.coinsContainer);
        const getViews = (c: Coin, isMain: boolean) => {
            const coin = new CoinView(this.scene, c.operation);
            coin.x = c.position.x;
            coin.y = c.position.y;
            this.coinsContainer.add(coin);
            return coin;
        };
        const dummyViews = dummyCoins.map((e) => getViews(e, false));
        const mainViews = mainCoins.map((e) => getViews(e, true));
        this.coinsSprites = [...dummyViews, ...mainViews];
        this.mainCoins = mainViews;
    }

    private initVines(points: Point[]) {
        points.forEach((e, i) => {
            const vine = new VineView(this.scene);
            vine.setPosition(e.x, e.y);
            this.slideContainer.add(vine);
            this.vines.push(vine);
        });
    }

    private initMonkey() {
        this.monkey = new MonkeyView(this.scene);
        this.add(this.monkey);
    }

    private initGround() {
        const lastPoint = this.simulate.newCurvePoints[this.simulate.newCurvePoints.length - 1];
        // todo add win logic
        // console.log(this.simulate);
        // const ground = this.scene.add.sprite(lastPoint.x, lastPoint.y + 100, 'game', 'landing_platform.png');
        // ground.setOrigin(0.5, 0);

        this.ground = new GroundView(this.scene, lastPoint.x, this.isWinning);
        this.ground.y = lastPoint.y + 140;
        this.slideContainer.add(this.ground);
    }

    private initPlayButton() {
        const { width, height } = this.scene.scale;
        this.playButton = this.scene.add.sprite(width / 2, height - 200, 'game', 'play.png');
        this.add(this.playButton);
        this.playButton.setInteractive({ cursor: 'pointer' });
        this.playButton.on(Phaser.Input.Events.POINTER_DOWN, this.handlePlayClick, this);
    }

    private handlePlayClick(): void {
        const result = this.getGameResult();
        this.isFirstPlay = false;
        this.isWinning = result.win;
        const target = result.value;
        const path = findShortestPath(target);
        if (path) {
            console.log('Path found:', path);
            path.forEach((step) => console.log(step));
            this.operations = path;
        } else {
            console.log(`No path could be found to reach ${result}.`);
        }

        console.table({
            multiplier: target.toFixed(1),
            won: this.isWinning,
        });

        this.playButton.setVisible(false);
        this.monkey?.destroy();
        this.ground?.destroy();
        this.startNew();
    }

    private getGameResult(): GameResult {
        let value, win;
        const gameParams = this.getQueryParam();
        if (this.isFirstPlay && gameParams) {
            win = gameParams.win;
            value = gameParams.value;
        } else {
            const { win: winning, value: target } = this.setRandomValue();
            win = winning;
            value = target;
            this.setQueryParam(this.isWinning, target);
        }
        return { win, value };
    }

    private setRandomValue(): { win: boolean; value: number } {
        return {
            win: Math.random() > 0.5,
            value: generateRandomTarget(),
        };
    }

    private setQueryParam(win: boolean, target: number): void {
        const url = new URL(window.location.href);
        const params = new URLSearchParams(url.search);
        params.set('win', `${win}`);
        params.set('value', `${target}`);
        window.history.pushState(null, '', `?${params.toString()}`);
    }

    private getQueryParam(): GameResult | null {
        const params = new URLSearchParams(window.location.search);
        const win = params.get('win');
        const value = params.get('value');
        if (win === null || value === null) return null;
        return { win: win === 'true', value: parseFloat(value) };
    }
}
